import jsPDF from "jspdf";
import { loadLogoForPdf } from "./logoLoader";

/**
 * Generate the official "Distinta gara" PDF in the format used by the
 * Delegazione Provinciale FIGC (see reference image).
 *
 * Sections:
 *  - Header: "DELEGAZIONE PROVINCIALE DI <city> S.S. <season> DISTINTA GIOCATORI"
 *  - Centered category title (e.g. "PRIMI CALCI 2016")
 *  - Match data box (date / time)
 *  - Field name & address, home team > away team, manifestazione, girone
 *  - Player table: N° maglia | Data Nascita (gg/mm/yyyy split) | Cognome e Nome
 *                  | C/Vt | Matricola FIGC | Documento | 1°T | 2°T | 3°T
 *  - Accompagnatori (staff + parents) with Tessera + Firma boxes
 *  - Allenatore / Istruttore footer
 */
export async function exportDistintaPdf(
  convocation,
  players,
  team,
  field,
  staff = [],
  parents = []
) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 12;
  let y = 10;

  // -------- header strip --------
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  const city = team.public_city || team.city || "_______";
  const season = convocation.season || team.season || _currentSeason();
  // Logo will be top-right (at x ~ 176-190, y 4-18). Keep header text away
  // from that band by clipping max width and centering on the left portion.
  const headerStr = `DELEGAZIONE PROVINCIALE DI ${String(city).toUpperCase()}    S.S. ${season}    DISTINTA GIOCATORI`;
  const headerMaxW = 158; // leaves ~18mm safety on the right for the logo
  const headerLine = doc.splitTextToSize(headerStr, headerMaxW)[0] || headerStr;
  doc.text(headerLine, (pageW - 22) / 2, y, { align: "center" });
  y += 5;

  // -------- title + date/time box --------
  const titleH = 14;
  // title cell
  doc.setLineWidth(0.4);
  doc.rect(margin, y, 130, titleH);
  doc.setFontSize(20);
  doc.setFont(undefined, "bold");
  const titleText = (team.category || convocation.title || "DISTINTA").toUpperCase();
  doc.text(titleText, margin + 65, y + 9, { align: "center" });
  // date/time box
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.rect(margin + 132, y, 64, titleH);
  doc.line(margin + 132, y + 7, margin + 196, y + 7);
  doc.text("Gara del", margin + 134, y + 4);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text(
    _fmtDate(convocation.match_date) || "",
    margin + 164,
    y + 5.5,
    { align: "center" }
  );
  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.text("Ore", margin + 134, y + 11);
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text(convocation.meeting_time || "", margin + 164, y + 12, {
    align: "center",
  });
  y += titleH + 4;

  // Logo top-right
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try {
      doc.addImage(logo.dataUrl, logo.format, pageW - margin - 22, 4, 14, 14);
    } catch (err) {
      console.warn("logo embed failed:", err);
    }
  }

  // -------- Stadio / Field row --------
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  const fieldLine = field
    ? `${field.name}${field.address ? ` - ${field.address}` : ""}`
    : convocation.location || "";
  doc.text("Stadio:", margin, y);
  doc.text(fieldLine, margin + 18, y);
  doc.line(margin + 17, y + 0.5, margin + 130, y + 0.5);
  doc.text("Città:", margin + 135, y);
  doc.text(team.public_city || team.city || "", margin + 150, y);
  doc.line(margin + 145, y + 0.5, pageW - margin, y + 0.5);
  y += 4;
  doc.setFontSize(7);
  doc.setTextColor(120);
  doc.text("Nome e indirizzo impianto sportivo", margin, y);
  doc.text("Città - Prov.", margin + 135, y);
  doc.setTextColor(0);
  y += 5;

  // -------- Squadra ospitante > Squadra ospitata --------
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  const us = team.name || "—";
  const them = convocation.opponent || "—";
  const isHome = (convocation.home_or_away || "home") === "home";
  const left = isHome ? us : them;
  const right = isHome ? them : us;
  doc.text(left, margin + 30, y);
  doc.text(">", pageW / 2, y, { align: "center" });
  doc.text(right, pageW - margin - 30, y, { align: "right" });
  if (convocation.girone) {
    doc.text(`(${convocation.girone})`, pageW - margin, y, { align: "right" });
  }
  y += 3.5;
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setTextColor(120);
  doc.text("Squadra ospitante", margin + 30, y);
  doc.text("Squadra ospitata", pageW - margin - 30, y, { align: "right" });
  doc.text("Girone", pageW - margin, y, { align: "right" });
  doc.setTextColor(0);
  y += 5;

  // Manifestazione + raggruppamento + elenco-squadra
  doc.setFontSize(9);
  doc.text("Manifestazione:", margin, y);
  doc.setFont(undefined, "bold");
  doc.text(convocation.manifestazione || "—", margin + 30, y);
  doc.setFont(undefined, "normal");
  doc.text(`Elenco giocatori Squadra: ${us}`, pageW - margin, y, {
    align: "right",
  });
  y += 5;

  // -------- Player table --------
  const colsX = [
    margin,        // X out
    margin + 8,    // N° maglia
    margin + 22,   // gg
    margin + 30,   // mm
    margin + 38,   // yyyy
    margin + 50,   // Cognome e Nome
    margin + 110,  // C/Vt
    margin + 120,  // Matricola FIGC
    margin + 148,  // Documento
    margin + 170,  // 1°T
    margin + 180,  // 2°T
    margin + 190,  // 3°T
  ];
  const rowH = 5.2;
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, pageW - 2 * margin, 6, "F");
  doc.text("", colsX[0] + 4, y + 4, { align: "center" });
  doc.text("N°", colsX[1] + 4, y + 4, { align: "center" });
  doc.text("Data Nascita", colsX[2] + 11, y + 4, { align: "center" });
  doc.text("Cognome e Nome", colsX[5] + 2, y + 4);
  doc.text("C/Vt", colsX[6] + 5, y + 4, { align: "center" });
  doc.text("Matricola FIGC", colsX[7] + 14, y + 4, { align: "center" });
  doc.text("Documento", colsX[8] + 11, y + 4, { align: "center" });
  doc.text("1°T", colsX[9] + 5, y + 4, { align: "center" });
  doc.text("2°T", colsX[10] + 5, y + 4, { align: "center" });
  doc.text("3°T", colsX[11] + 5, y + 4, { align: "center" });
  doc.setFont(undefined, "normal");
  y += 6;

  const selectedIds = new Set(convocation.player_ids || []);
  const rows = [...players].sort((a, b) => {
    const an = `${a.last_name || ""} ${a.first_name || ""}`.toLowerCase();
    const bn = `${b.last_name || ""} ${b.first_name || ""}`.toLowerCase();
    return an.localeCompare(bn, "it");
  });

  for (const p of rows) {
    if (y > pageH - 50) {
      doc.addPage();
      y = 15;
    }
    // outline row
    doc.setLineWidth(0.2);
    doc.rect(margin, y, pageW - 2 * margin, rowH);
    // column dividers
    for (let i = 1; i < colsX.length; i++) {
      const x = colsX[i];
      doc.line(x, y, x, y + rowH);
    }
    // X mark for non-convocati
    const isCalled = selectedIds.size === 0 || selectedIds.has(p.id);
    if (!isCalled) {
      doc.setFont(undefined, "bold");
      doc.text("X", colsX[0] + 4, y + 3.8, { align: "center" });
      doc.setFont(undefined, "normal");
    }
    // N° maglia
    doc.setFont(undefined, "bold");
    doc.text(String(p.jersey_number ?? ""), colsX[1] + 4, y + 3.8, {
      align: "center",
    });
    doc.setFont(undefined, "normal");
    // birth date split
    const [yyyy, mm, gg] = (p.birth_date || "").split("-");
    doc.text(gg || "", colsX[2] + 4, y + 3.8, { align: "center" });
    doc.text(mm || "", colsX[3] + 4, y + 3.8, { align: "center" });
    doc.text(yyyy || "", colsX[4] + 6, y + 3.8, { align: "center" });
    // Cognome e nome
    const fullName = `${(p.last_name || "").toUpperCase()} ${p.first_name || ""}`.trim();
    doc.text(_truncate(fullName, 38), colsX[5] + 1.5, y + 3.8);
    // Matricola FIGC
    doc.text(p.figc_id || "", colsX[7] + 14, y + 3.8, { align: "center" });
    // Documento
    doc.text(p.figc_document || "CRT-SGS", colsX[8] + 11, y + 3.8, {
      align: "center",
    });
    y += rowH;
  }

  // Fill remaining blank lines up to ~22 rows for handwritten additions
  while (y < pageH - 80 && rows.length < 22) {
    if (y > pageH - 80) break;
    doc.setLineWidth(0.2);
    doc.rect(margin, y, pageW - 2 * margin, rowH);
    for (let i = 1; i < colsX.length; i++) {
      doc.line(colsX[i], y, colsX[i], y + rowH);
    }
    y += rowH;
    rows.push({});
  }

  y += 4;

  // -------- Dirigenti accompagnatori section --------
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.text("Dirigenti accompagnatori e staff", margin, y);
  y += 3;
  doc.setFontSize(7);
  doc.setFont(undefined, "normal");
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, pageW - 2 * margin, 5, "F");
  doc.text("Qualifica", margin + 2, y + 3.5);
  doc.text("Nome e cognome", margin + 35, y + 3.5);
  doc.text("Tessera FIGC", margin + 110, y + 3.5);
  doc.text("Firma", margin + 145, y + 3.5);
  y += 5;

  // Build accompagnatori list from selection
  const accompKeys = convocation.accompagnatori_ids || [];
  const accompList = [];
  for (const key of accompKeys) {
    const [kind, id] = String(key).split(":");
    if (kind === "s") {
      const s = staff.find((x) => x.id === id);
      if (s) {
        accompList.push({
          qualif: s.role || "Dirig. Accomp",
          name: s.name,
          tessera: s.figc_card_number || "",
        });
      }
    } else if (kind === "p") {
      const g = parents.find((x) => x.id === id);
      if (g) {
        accompList.push({
          qualif: "Dirig. Accomp.",
          name: `${(g.last_name || "").toUpperCase()} ${g.first_name || ""}`.trim(),
          tessera: g.fiscal_code || "",
        });
      }
    }
  }
  // pad to 6 rows
  while (accompList.length < 6) accompList.push({ qualif: "", name: "", tessera: "" });

  for (const a of accompList) {
    doc.rect(margin, y, pageW - 2 * margin, 5.5);
    doc.line(margin + 33, y, margin + 33, y + 5.5);
    doc.line(margin + 105, y, margin + 105, y + 5.5);
    doc.line(margin + 140, y, margin + 140, y + 5.5);
    doc.setFontSize(7);
    doc.text(a.qualif || "", margin + 2, y + 3.7);
    doc.setFont(undefined, "bold");
    doc.text(a.name || "", margin + 35, y + 3.7);
    doc.setFont(undefined, "normal");
    doc.text(a.tessera || "", margin + 110, y + 3.7);
    y += 5.5;
  }
  y += 3;

  // -------- Allenatore / Istruttore section --------
  if (y > pageH - 25) {
    doc.addPage();
    y = 15;
  }
  const coaches = (staff || []).filter((s) =>
    ["Allenatore", "Vice Allenatore", "Preparatore atletico", "Preparatore portieri"].includes(s.role)
  );
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  doc.text("Tecnici", margin, y);
  y += 3;
  doc.setFont(undefined, "normal");
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, pageW - 2 * margin, 5, "F");
  doc.text("Qualifica", margin + 2, y + 3.5);
  doc.text("Nome e cognome", margin + 35, y + 3.5);
  doc.text("Tessera FIGC e documento d'identità", margin + 110, y + 3.5);
  y += 5;
  const techRows = coaches.length ? coaches.slice(0, 3) : Array.from({ length: 2 }).map(() => ({}));
  while (techRows.length < 2) techRows.push({});
  for (const t of techRows) {
    doc.rect(margin, y, pageW - 2 * margin, 5.5);
    doc.line(margin + 33, y, margin + 33, y + 5.5);
    doc.line(margin + 105, y, margin + 105, y + 5.5);
    doc.setFontSize(7);
    doc.text(t.role || "Istruttore", margin + 2, y + 3.7);
    doc.setFont(undefined, "bold");
    doc.text(t.name || "", margin + 35, y + 3.7);
    doc.setFont(undefined, "normal");
    doc.text(t.figc_card_number || "", margin + 110, y + 3.7);
    y += 5.5;
  }
  y += 5;

  // -------- Disclaimer --------
  doc.setFontSize(6.5);
  doc.setTextColor(80);
  const disclaimer =
    "Il sottoscritto dirigente accompagnatore dichiara che gli atleti soprannominati sono regolarmente tesserati e " +
    "assicurati, assumendosi ogni responsabilità della loro fisica idoneità all'attività sportiva, vista la normativa vigente art. 43 N.O.I.F.";
  const lines = doc.splitTextToSize(disclaimer, pageW - 2 * margin);
  doc.text(lines, margin, y);
  doc.setTextColor(0);

  const safe = String(convocation.title || "distinta")
    .replace(/[^\w\s-]+/g, "")
    .trim()
    .replace(/\s+/g, "_");
  doc.save(`distinta_${safe}_${convocation.match_date || ""}.pdf`);
}

function _fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y) return iso;
  return `${d}/${m}/${y}`;
}

function _truncate(s, n) {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

function _currentSeason() {
  const now = new Date();
  const yr = now.getFullYear();
  const m = now.getMonth();
  const start = m >= 7 ? yr : yr - 1;
  return `${start}/${start + 1}`;
}
